/**
 * Fallout-style conversation panel: NPC portrait + name/role, body text, and
 * a numbered list of reply options. Plain DOM + a scoped style block.
 *
 * Styling lives entirely in `ui/styles.ts` under the `bv-dialog-*` selectors,
 * which moved onto the Salvage Codex tokens on 2026-09-11 — the name is set in
 * `--font-display`, the body in `--font-body` (the one place in the overlay
 * layer carrying whole sentences), and the replies in `--font-technical`. This
 * file emits structure only; keep it that way, and keep the show/hide API
 * stable for index.ts.
 */
import type { DialogNode, Npc } from '../types';
import { actionIconSvg } from './action-bar';
import { NpcViewer } from './npc-viewer';

/**
 * The glyph and tooltip for an option that DOES something rather than says
 * something. The icons are the action bar's own — the price tag is Shop, the
 * ruled page is Quest Journal — so a player reads them as the same things.
 * `kind` is derived server-side from the reply's outcomes; the client never
 * guesses from label text.
 */
const KIND_ICON = {
  trade: { icon: 'tag', title: 'Trade' },
  mission: { icon: 'journal', title: 'Mission' },
} as const;

/** The two badges the action bar has no icon for: Accept (a tick) and Locked (a padlock). */
const ACCEPT_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';
const LOCK_SVG = '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M7 10V8a5 5 0 0110 0v2h1.5A1.5 1.5 0 0120 11.500v8a1.500 1.500 0 01-1.500 1.500h-13A1.500 1.500 0 014 19.500v-8A1.500 1.500 0 015.500 10H7zm2 0h6V8a3 3 0 00-6 0v2z"/></svg>';

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  })[c]!);
}

export interface DialogHandlers {
  onChoose(optionIndex: number): void;
  onClose(): void;
}

export class DialogUI {
  readonly el: HTMLDivElement;
  private handlers: DialogHandlers | null = null;
  /** Small standalone WebGL viewer for the NPC's 3D body — created on show(), disposed on hide(). */
  private viewer: NpcViewer | null = null;

  constructor(parent: HTMLElement) {
    this.el = document.createElement('div');
    this.el.className = 'bv-dialog';
    this.el.hidden = true;
    this.el.innerHTML = `
      <div class="bv-dialog-panel">
        <div class="bv-dialog-portrait grained">
          <div class="bv-dialog-frame">
            <img data-portrait alt="" />
            <div class="bv-npc-viewer" data-viewer></div>
          </div>
        </div>
        <div class="bv-dialog-body">
          <div class="bv-dialog-head">
            <span class="bv-dialog-name" data-name></span>
            <span class="bv-dialog-role" data-role></span>
          </div>
          <p class="bv-dialog-text" data-text></p>
          <div class="bv-dialog-card" data-card hidden></div>
          <ol class="bv-dialog-options" data-options></ol>
        </div>
      </div>`;
    parent.appendChild(this.el);
  }

  get isOpen(): boolean {
    return !this.el.hidden;
  }

  /**
   * `bodyModelUrl` is the NPC's placed body GLB (resolved by the caller from
   * the world objects — `Npc.bodyModelUrl` is unset for settlement NPCs). With
   * it, the portrait box shows the real rigged body playing its idle; without
   * it (or if the load fails), NpcViewer leaves the WebGL viewport empty and
   * the 2D portrait `<img>` behind it is the only thing shown.
   */
  show(npc: Npc, node: DialogNode, handlers: DialogHandlers, bodyModelUrl?: string): void {
    this.handlers = handlers;
    const portrait = this.el.querySelector('[data-portrait]') as HTMLImageElement;
    portrait.src = npc.portrait;
    this.setText('[data-name]', npc.name);
    this.setText('[data-role]', npc.role);
    this.render(node);
    this.el.hidden = false;

    // NPC is constant for the whole conversation — mount once here, not per node.
    this.viewer?.dispose();
    const viewerHost = this.el.querySelector('[data-viewer]') as HTMLElement;
    this.viewer = new NpcViewer();
    // The 2D portrait is the FALLBACK, not a backdrop. It starts visible so an
    // NPC whose body fails to load still has a face, and is taken down the
    // instant the real rigged body is in the scene — otherwise the shipped
    // placeholder PNG (a default survivor in a T-POSE) shows through at 55%
    // opacity behind the posed model, which reads as two characters.
    portrait.hidden = false;
    this.viewer.mount(
      viewerHost,
      npc.id,
      bodyModelUrl ?? npc.bodyModelUrl,
      () => { portrait.hidden = true; },
    );
  }

  /** Advance to a new node without re-opening (same NPC). */
  render(node: DialogNode): void {
    this.setText('[data-text]', node.text);
    const card = this.el.querySelector('[data-card]') as HTMLElement;
    card.hidden = !node.card;
    card.innerHTML = node.card
      ? `<div class="bv-dialog-card-title">${esc(node.card.title)}</div>`
        + `<ul>${node.card.objectives.map((o) => `<li>${esc(o)}</li>`).join('')}</ul>`
        + `<div class="bv-dialog-card-reward"><span>Reward</span>${esc(node.card.reward)}</div>`
      : '';
    const list = this.el.querySelector('[data-options]') as HTMLOListElement;
    list.innerHTML = '';
    node.options.forEach((opt, i) => {
      const li = document.createElement('li');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bv-dialog-opt';

      const num = document.createElement('span');
      num.className = 'bv-dialog-num';
      num.textContent = String(i + 1);
      const label = document.createElement('span');
      label.className = 'bv-dialog-label';
      label.textContent = opt.label;
      btn.append(num, label);

      if (opt.locked) {
        // Visible but dead: the reason is on the button, and the click and the
        // number key both ignore it (the server refuses it as well).
        btn.disabled = true;
        btn.dataset.locked = 'true';
        btn.title = opt.locked;
        label.insertAdjacentHTML('afterend', `<span class="bv-dialog-lock-note">${esc(opt.locked)}</span>`);
        const badge = document.createElement('span');
        badge.className = 'bv-dialog-kind bv-dialog-kind-lock';
        badge.innerHTML = LOCK_SVG;
        btn.appendChild(badge);
      }
      const kind = opt.kind && opt.kind !== 'accept' ? KIND_ICON[opt.kind] : undefined;
      if (opt.kind === 'accept') {
        btn.dataset.kind = 'accept';
        const badge = document.createElement('span');
        badge.className = 'bv-dialog-kind';
        badge.title = 'Accept';
        badge.innerHTML = ACCEPT_SVG;
        btn.appendChild(badge);
      } else if (opt.kind && kind && !opt.locked) {
        btn.dataset.kind = opt.kind;
        const badge = document.createElement('span');
        badge.className = 'bv-dialog-kind';
        badge.title = kind.title;
        // Static markup from the action bar's icon table — no server text in it.
        badge.innerHTML = actionIconSvg(kind.icon);
        btn.appendChild(badge);
      }
      btn.addEventListener('click', () => this.handlers?.onChoose(i));
      li.appendChild(btn);
      list.appendChild(li);
    });
  }

  hide(): void {
    this.el.hidden = true;
    this.handlers = null;
    this.viewer?.dispose();
    this.viewer = null;
  }

  /** Keyboard support: 1–9 picks an option, Esc closes. Returns handled. */
  handleKey(key: string): boolean {
    if (!this.isOpen) return false;
    if (key === 'Escape') {
      this.handlers?.onClose();
      return true;
    }
    const n = Number(key);
    if (Number.isInteger(n) && n >= 1) {
      const list = this.el.querySelector('[data-options]') as HTMLOListElement;
      if (n <= list.children.length) {
        if ((list.children[n - 1]!.querySelector('button') as HTMLButtonElement).disabled) return true;
        this.handlers?.onChoose(n - 1);
        return true;
      }
    }
    return false;
  }

  private setText(sel: string, text: string): void {
    const node = this.el.querySelector(sel);
    if (node) node.textContent = text;
  }

  dispose(): void {
    // hide() disposes the NPC viewer — must run even if a conversation is
    // still open when the view is torn down (e.g. SPA navigation mid-chat).
    this.hide();
    this.el.remove();
  }
}
